import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getShopById, updateShop, getHomeServices, addHomeService, updateHomeService, deleteHomeService, toggleHomeService } from "../utils/api";
import axios from "axios";

const API = axios.create({ baseURL: process.env.REACT_APP_API_URL || "http://localhost:5000/api" });
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const CLOUD_NAME = "dvxt5fnic";
const UPLOAD_PRESET = "oyxievyx";

export default function EditShop() {
  const navigate = useNavigate();
  const { shopId } = useParams();

  const [shopForm, setShopForm] = useState({ name: "", description: "", address: "", city: "", phone: "", opening_time: "09:00", closing_time: "21:00" });
  const [services, setServices] = useState([]);
  const [newService, setNewService] = useState({ name: "", description: "", duration: 30, price: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeTab, setActiveTab] = useState("details");

  const [currentPhoto, setCurrentPhoto] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);

  const [homeServiceEnabled, setHomeServiceEnabled] = useState(false);
  const [homeServices, setHomeServices] = useState([]);
  const [newHomeService, setNewHomeService] = useState({ name: "", description: "", duration: 30, price: "", advance_amount: "" });
  const [homeServiceSaving, setHomeServiceSaving] = useState(false);
  const [editingHomeService, setEditingHomeService] = useState(null);

  useEffect(() => {
    const fetchShop = async () => {
      try {
        const res = await getShopById(shopId);
        const shopData = res.data.shop || res.data;
        const servicesData = res.data.services || [];
        setShopForm({
          name: shopData.name || "",
          description: shopData.description || "",
          address: shopData.address || "",
          city: shopData.city || "",
          phone: shopData.phone || "",
          opening_time: shopData.opening_time?.slice(0, 5) || "09:00",
          closing_time: shopData.closing_time?.slice(0, 5) || "21:00",
        });
        setCurrentPhoto(shopData.photo || null);
        setHomeServiceEnabled(shopData.home_service_enabled || false);
        setServices(servicesData);
      } catch (err) { setError("Could not load shop details."); }
      finally { setLoading(false); }
    };
    fetchShop();
  }, [shopId]);

  useEffect(() => {
    if (activeTab === "homeservices") fetchHomeServices();
  }, [activeTab]);

  const fetchHomeServices = async () => {
    try {
      const res = await getHomeServices(shopId);
      const data = res.data;
      setHomeServices(Array.isArray(data.services) ? data.services : Array.isArray(data) ? data : []);
    } catch { setHomeServices([]); }
  };

  const handleSaveDetails = async () => {
    setSaving(true); setError(""); setSuccess("");
    try {
      await updateShop(shopId, shopForm);
      setSuccess("Shop details updated! ✅");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) { setError(err.response?.data?.message || "Could not update shop."); }
    finally { setSaving(false); }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError("Photo must be less than 5MB."); return; }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));
    setError("");
  };

  const handleUploadPhoto = async () => {
    if (!photoFile) return;
    setPhotoUploading(true); setError(""); setSuccess("");
    try {
      const formData = new FormData();
      formData.append("file", photoFile);
      formData.append("upload_preset", UPLOAD_PRESET);
      formData.append("folder", "trimspot/shops");
      const cloudRes = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`, { method: "POST", body: formData });
      const cloudData = await cloudRes.json();
      if (!cloudData.secure_url) throw new Error("Upload failed");
      const token = localStorage.getItem("token");
      await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:5000/api"}/upload/shop-photo`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ photoUrl: cloudData.secure_url, shopId }),
      });
      setCurrentPhoto(cloudData.secure_url);
      setPhotoFile(null);
      setPhotoPreview(null);
      setSuccess("Photo updated successfully! ✅");
      setTimeout(() => setSuccess(""), 3000);
    } catch { setError("Could not upload photo. Please try again."); }
    finally { setPhotoUploading(false); }
  };

  const handleAddService = async () => {
    if (!newService.name || !newService.price) { setError("Service name and price are required."); return; }
    setSaving(true); setError("");
    try {
      const res = await API.post(`/shops/${shopId}/services`, newService);
      setServices((prev) => [...prev, res.data.service]);
      setNewService({ name: "", description: "", duration: 30, price: "" });
      setSuccess("Service added! ✅");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) { setError(err.response?.data?.message || "Could not add service."); }
    finally { setSaving(false); }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await API.delete(`/shops/${shopId}/services/${serviceId}`);
      setServices((prev) => prev.filter((s) => s.id !== serviceId));
      setSuccess("Service deleted! ✅");
      setTimeout(() => setSuccess(""), 3000);
    } catch { setError("Could not delete service."); }
  };

  const handleUpdateServicePrice = async (serviceId, newPrice) => {
    try {
      await API.put(`/shops/${shopId}/services/${serviceId}`, { price: newPrice });
      setServices((prev) => prev.map((s) => s.id === serviceId ? { ...s, price: newPrice } : s));
    } catch { setError("Could not update price."); }
  };

  const handleToggleHomeService = async () => {
    const newVal = !homeServiceEnabled;
    try {
      await toggleHomeService(shopId, newVal);
      setHomeServiceEnabled(newVal);
      setSuccess(`Home service ${newVal ? "enabled" : "disabled"}! ✅`);
      setTimeout(() => setSuccess(""), 3000);
    } catch { setError("Could not update home service status."); }
  };

  const handleAddHomeService = async () => {
    if (!newHomeService.name || !newHomeService.price || !newHomeService.advance_amount) {
      setError("Name, price and advance amount are required."); return;
    }
    setHomeServiceSaving(true); setError("");
    try {
      await addHomeService({ ...newHomeService, shop_id: Number(shopId) });
      await fetchHomeServices();
      setNewHomeService({ name: "", description: "", duration: 30, price: "", advance_amount: "" });
      setSuccess("Home service added! ✅");
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(err.response?.data?.message || "Could not add home service.");
    } finally {
      setHomeServiceSaving(false);
    }
  };

  const handleDeleteHomeService = async (id) => {
    try {
      await deleteHomeService(id);
      setHomeServices((prev) => prev.filter((s) => s.id !== id));
      setSuccess("Home service deleted! ✅");
      setTimeout(() => setSuccess(""), 3000);
    } catch { setError("Could not delete home service."); }
  };

  const handleUpdateHomeService = async (id) => {
    const svc = homeServices.find((s) => s.id === id);
    if (!svc) return;
    try {
      await updateHomeService(id, { price: svc.price, advance_amount: svc.advance_amount });
      setEditingHomeService(null);
      setSuccess("Home service updated! ✅");
      setTimeout(() => setSuccess(""), 3000);
    } catch { setError("Could not update home service."); }
  };

  if (loading) return <div style={styles.center}><p style={{ color: "var(--text-muted)" }}>Loading...</p></div>;

  return (
    <div style={styles.page}>

      {/* Header */}
      <div style={styles.header}>
        <button onClick={() => navigate("/dashboard")} style={styles.backBtn}>← Dashboard</button>
        <h2 style={styles.headerTitle}>✏️ Edit Shop</h2>
        <div />
      </div>

      {/* Tabs — constrained to 600px like the content */}
      <div style={styles.tabRow}>
        <div style={styles.tabInner}>
          <button onClick={() => setActiveTab("details")} style={{ ...styles.tab, ...(activeTab === "details" ? styles.tabActive : {}) }}>
            🏪 Details
          </button>
          <button onClick={() => setActiveTab("photo")} style={{ ...styles.tab, ...(activeTab === "photo" ? styles.tabActive : {}) }}>
            📷 Photo
          </button>
          <button onClick={() => setActiveTab("services")} style={{ ...styles.tab, ...(activeTab === "services" ? styles.tabActive : {}) }}>
            ✂️ Services
          </button>
          <button onClick={() => setActiveTab("homeservices")} style={{ ...styles.tab, ...(activeTab === "homeservices" ? styles.tabActiveGreen : {}) }}>
            🏠 Home
          </button>
        </div>
      </div>

      <div style={styles.container}>
        {error && <div style={styles.errorBox}>{error}</div>}
        {success && <div style={styles.successBox}>{success}</div>}

        {/* ── SHOP DETAILS TAB ── */}
        {activeTab === "details" && (
          <div style={styles.formCard}>
            <h3 style={styles.sectionTitle}>Shop Information</h3>
            <label style={styles.label}>Shop Name</label>
            <input value={shopForm.name} onChange={(e) => setShopForm({ ...shopForm, name: e.target.value })} style={styles.input} placeholder="Shop name" />
            <label style={styles.label}>Description</label>
            <textarea value={shopForm.description} onChange={(e) => setShopForm({ ...shopForm, description: e.target.value })} style={styles.textarea} rows={3} placeholder="About your shop..." />
            <label style={styles.label}>Address</label>
            <input value={shopForm.address} onChange={(e) => setShopForm({ ...shopForm, address: e.target.value })} style={styles.input} placeholder="Full address" />
            <label style={styles.label}>City</label>
            <input value={shopForm.city} onChange={(e) => setShopForm({ ...shopForm, city: e.target.value })} style={styles.input} placeholder="City" />
            <label style={styles.label}>Phone</label>
            <input value={shopForm.phone} onChange={(e) => setShopForm({ ...shopForm, phone: e.target.value })} style={styles.input} placeholder="Phone number" />
            <label style={styles.label}>Working Hours</label>
            <div style={styles.timeRow}>
              <div style={styles.timeGroup}>
                <span style={styles.timeLabel}>Opens at</span>
                <input type="time" value={shopForm.opening_time} onChange={(e) => setShopForm({ ...shopForm, opening_time: e.target.value })} style={styles.timeInput} />
              </div>
              <span style={{ color: "var(--text-muted)", fontSize: "20px", marginTop: "16px" }}>→</span>
              <div style={styles.timeGroup}>
                <span style={styles.timeLabel}>Closes at</span>
                <input type="time" value={shopForm.closing_time} onChange={(e) => setShopForm({ ...shopForm, closing_time: e.target.value })} style={styles.timeInput} />
              </div>
            </div>
            <button onClick={handleSaveDetails} style={styles.saveBtn} disabled={saving}>
              {saving ? "Saving..." : "💾 Save Changes"}
            </button>
          </div>
        )}

        {/* ── PHOTO TAB ── */}
        {activeTab === "photo" && (
          <div style={styles.formCard}>
            <h3 style={styles.sectionTitle}>Shop Photo</h3>
            <p style={{ color: "var(--text-muted)", fontSize: "13px", marginBottom: "20px" }}>
              This photo will be shown to customers on your shop listing. Use a clear, bright photo of your shop!
            </p>
            {currentPhoto && !photoPreview && (
              <div style={styles.currentPhotoWrap}>
                <p style={styles.currentPhotoLabel}>📸 Current Photo</p>
                <img src={currentPhoto} alt="Current shop" style={styles.currentPhoto} />
                <button onClick={() => setCurrentPhoto(null)} style={styles.removeCurrentBtn}>🗑️ Remove Photo</button>
              </div>
            )}
            {photoPreview && (
              <div style={styles.currentPhotoWrap}>
                <p style={styles.currentPhotoLabel}>👁️ New Photo Preview</p>
                <img src={photoPreview} alt="New shop preview" style={styles.currentPhoto} />
                <div style={{ display: "flex", gap: "8px", marginTop: "10px" }}>
                  <button onClick={handleUploadPhoto} style={styles.uploadPhotoBtn} disabled={photoUploading}>
                    {photoUploading ? "Uploading..." : "✅ Save This Photo"}
                  </button>
                  <button onClick={() => { setPhotoFile(null); setPhotoPreview(null); }} style={styles.cancelPhotoBtn}>✕ Cancel</button>
                </div>
              </div>
            )}
            {!photoPreview && (
              <label htmlFor="editShopPhoto" style={styles.photoUploadLabel}>
                <span style={{ fontSize: "36px" }}>📷</span>
                <span style={styles.photoUploadText}>{currentPhoto ? "Click to change shop photo" : "Click to upload shop photo"}</span>
                <span style={styles.photoUploadHint}>JPG, PNG • Max 5MB</span>
              </label>
            )}
            <input id="editShopPhoto" type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: "none" }} />
          </div>
        )}

        {/* ── SERVICES TAB ── */}
        {activeTab === "services" && (
          <div>
            <div style={styles.formCard}>
              <h3 style={styles.sectionTitle}>Current Services</h3>
              {services.length === 0 ? (
                <p style={{ color: "var(--text-muted)", textAlign: "center", padding: "20px" }}>No services yet. Add one below!</p>
              ) : (
                services.map((service) => (
                  <div key={service.id} style={styles.serviceRow}>
                    <div style={styles.serviceInfo}>
                      <p style={styles.serviceName}>{service.name}</p>
                      <p style={styles.serviceMeta}>⏱ {service.duration} mins</p>
                    </div>
                    <div style={styles.serviceActions}>
                      <div style={styles.priceEdit}>
                        <span style={{ color: "var(--text-muted)", fontSize: "12px" }}>₹</span>
                        <input
                          type="number"
                          value={service.price}
                          onChange={(e) => setServices((prev) => prev.map((s) => s.id === service.id ? { ...s, price: e.target.value } : s))}
                          onBlur={(e) => handleUpdateServicePrice(service.id, e.target.value)}
                          style={styles.priceInput}
                        />
                      </div>
                      <button onClick={() => handleDeleteService(service.id)} style={styles.deleteBtn}>🗑️</button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div style={styles.formCard}>
              <h3 style={styles.sectionTitle}>+ Add New Service</h3>
              <label style={styles.label}>Service Name *</label>
              <input placeholder="e.g. Haircut" value={newService.name} onChange={(e) => setNewService({ ...newService, name: e.target.value })} style={styles.input} />
              <label style={styles.label}>Description</label>
              <input placeholder="Short description (optional)" value={newService.description} onChange={(e) => setNewService({ ...newService, description: e.target.value })} style={styles.input} />
              <div style={{ display: "flex", gap: "12px" }}>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Duration (mins) *</label>
                  <select value={newService.duration} onChange={(e) => setNewService({ ...newService, duration: Number(e.target.value) })} style={styles.input}>
                    {[15, 20, 30, 45, 60, 90, 120].map((d) => <option key={d} value={d}>{d} mins</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Price (₹) *</label>
                  <input type="number" placeholder="e.g. 100" value={newService.price} onChange={(e) => setNewService({ ...newService, price: e.target.value })} style={styles.input} min="0" />
                </div>
              </div>
              <button onClick={handleAddService} style={styles.saveBtn} disabled={saving}>{saving ? "Adding..." : "+ Add Service"}</button>
            </div>
          </div>
        )}

        {/* ── HOME SERVICES TAB ── */}
        {activeTab === "homeservices" && (
          <div>
            <div style={styles.formCard}>
              <h3 style={styles.sectionTitle}>🏠 Home Service</h3>
              <p style={{ color: "var(--text-muted)", fontSize: "13px", marginBottom: "20px" }}>
                When enabled, customers will see a "Home service available" badge on your shop card and can request a home visit.
              </p>
              <div style={styles.toggleRow}>
                <div>
                  <p style={{ color: "white", fontWeight: "600", fontSize: "14px", margin: 0 }}>Home Service</p>
                  <p style={{ color: "var(--text-muted)", fontSize: "12px", margin: "4px 0 0" }}>
                    {homeServiceEnabled ? "✅ Currently active — visible to customers" : "❌ Currently off — customers cannot see it"}
                  </p>
                </div>
                <button onClick={handleToggleHomeService} style={homeServiceEnabled ? styles.toggleBtnOn : styles.toggleBtnOff}>
                  {homeServiceEnabled ? "Turn Off" : "Turn On"}
                </button>
              </div>
            </div>

            <div style={styles.formCard}>
              <h3 style={styles.sectionTitle}>Home Service Offerings</h3>
              {homeServices.length === 0 ? (
                <p style={{ color: "var(--text-muted)", textAlign: "center", padding: "20px" }}>No home services added yet. Add one below!</p>
              ) : (
                homeServices.filter(Boolean).map((svc) => (
                  <div key={svc.id} style={styles.homeServiceRow}>
                    <div style={styles.serviceInfo}>
                      <p style={styles.serviceName}>{svc.name}</p>
                      <p style={styles.serviceMeta}>⏱ {svc.duration} mins</p>
                      {svc.description && <p style={{ ...styles.serviceMeta, marginTop: "2px" }}>{svc.description}</p>}
                    </div>
                    {editingHomeService === svc.id ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-end" }}>
                        <div style={styles.priceEdit}>
                          <span style={{ color: "var(--text-muted)", fontSize: "11px" }}>Price ₹</span>
                          <input type="number" value={svc.price} onChange={(e) => setHomeServices((prev) => prev.map((s) => s.id === svc.id ? { ...s, price: e.target.value } : s))} style={{ ...styles.priceInput, width: "55px" }} />
                        </div>
                        <div style={styles.priceEdit}>
                          <span style={{ color: "var(--text-muted)", fontSize: "11px" }}>Adv ₹</span>
                          <input type="number" value={svc.advance_amount} onChange={(e) => setHomeServices((prev) => prev.map((s) => s.id === svc.id ? { ...s, advance_amount: e.target.value } : s))} style={{ ...styles.priceInput, width: "55px" }} />
                        </div>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button onClick={() => handleUpdateHomeService(svc.id)} style={styles.saveMiniBtn}>✅</button>
                          <button onClick={() => setEditingHomeService(null)} style={styles.cancelMiniBtn}>✕</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "6px" }}>
                        <span style={styles.greenPrice}>₹{svc.price}</span>
                        <span style={{ color: "var(--text-muted)", fontSize: "11px" }}>Advance: ₹{svc.advance_amount}</span>
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button onClick={() => setEditingHomeService(svc.id)} style={styles.editBtnSmall}>✏️</button>
                          <button onClick={() => handleDeleteHomeService(svc.id)} style={styles.deleteBtn}>🗑️</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            <div style={styles.formCard}>
              <h3 style={styles.sectionTitle}>+ Add Home Service</h3>
              <label style={styles.label}>Service Name *</label>
              <input placeholder="e.g. Home Haircut" value={newHomeService.name} onChange={(e) => setNewHomeService({ ...newHomeService, name: e.target.value })} style={styles.input} />
              <label style={styles.label}>Description</label>
              <input placeholder="Short description (optional)" value={newHomeService.description} onChange={(e) => setNewHomeService({ ...newHomeService, description: e.target.value })} style={styles.input} />
              <div style={{ display: "flex", gap: "12px" }}>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Duration (mins) *</label>
                  <select value={newHomeService.duration} onChange={(e) => setNewHomeService({ ...newHomeService, duration: Number(e.target.value) })} style={styles.input}>
                    {[15, 20, 30, 45, 60, 90, 120].map((d) => <option key={d} value={d}>{d} mins</option>)}
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={styles.label}>Price (₹) *</label>
                  <input type="number" placeholder="e.g. 300" value={newHomeService.price} onChange={(e) => setNewHomeService({ ...newHomeService, price: e.target.value })} style={styles.input} min="0" />
                </div>
              </div>
              <label style={styles.label}>Advance Amount (₹) *</label>
              <input type="number" placeholder="e.g. 100 — customer pays this upfront" value={newHomeService.advance_amount} onChange={(e) => setNewHomeService({ ...newHomeService, advance_amount: e.target.value })} style={styles.input} min="0" />
              <p style={{ color: "var(--text-muted)", fontSize: "12px", marginTop: "-12px", marginBottom: "16px" }}>
                Customer pays this advance online. Rest is collected at doorstep.
              </p>
              <button onClick={handleAddHomeService} style={styles.greenSaveBtn} disabled={homeServiceSaving}>
                {homeServiceSaving ? "Adding..." : "🏠 Add Home Service"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "var(--bg-primary)", paddingBottom: "40px" },
  center: { display: "flex", alignItems: "center", justifyContent: "center", minHeight: "80vh" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", background: "var(--bg-secondary)", borderBottom: "1px solid var(--border)" },
  headerTitle: { fontSize: "17px", fontWeight: "bold", color: "white" },
  backBtn: { background: "none", border: "1px solid var(--border)", color: "var(--text-secondary)", padding: "8px 14px", borderRadius: "6px", cursor: "pointer", fontSize: "14px" },
  tabRow: { background: "var(--bg-secondary)", borderBottom: "1px solid var(--border)" },
  tabInner: { maxWidth: "600px", margin: "0 auto", display: "flex", width: "100%" },
  tab: { flex: 1, padding: "14px", border: "none", background: "none", cursor: "pointer", fontSize: "13px", color: "var(--text-muted)" },
  tabActive: { color: "var(--accent)", fontWeight: "bold", borderBottom: "2px solid var(--accent)", background: "rgba(233,69,96,0.05)" },
  tabActiveGreen: { color: "#4caf50", fontWeight: "bold", borderBottom: "2px solid #4caf50", background: "rgba(76,175,80,0.05)" },
  container: { maxWidth: "600px", margin: "0 auto", padding: "20px 16px", width: "100%", boxSizing: "border-box" },
  errorBox: { background: "rgba(233,69,96,0.1)", color: "#ff6b6b", padding: "12px", borderRadius: "8px", marginBottom: "16px", border: "1px solid rgba(233,69,96,0.3)" },
  successBox: { background: "rgba(76,175,80,0.1)", color: "#4caf50", padding: "12px", borderRadius: "8px", marginBottom: "16px", border: "1px solid rgba(76,175,80,0.3)" },
  formCard: { background: "var(--bg-card)", borderRadius: "16px", padding: "20px", marginBottom: "16px", border: "1px solid var(--border)" },
  sectionTitle: { fontSize: "15px", fontWeight: "bold", color: "white", marginBottom: "16px" },
  label: { display: "block", color: "var(--text-secondary)", fontSize: "13px", fontWeight: "500", marginBottom: "6px" },
  input: { width: "100%", padding: "11px 13px", borderRadius: "8px", fontSize: "14px", marginBottom: "16px", boxSizing: "border-box" },
  textarea: { width: "100%", padding: "11px 13px", borderRadius: "8px", fontSize: "14px", marginBottom: "16px", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" },
  timeRow: { display: "flex", gap: "12px", alignItems: "center", marginBottom: "20px", flexWrap: "wrap" },
  timeGroup: { flex: 1, minWidth: "120px" },
  timeLabel: { display: "block", color: "var(--text-muted)", fontSize: "12px", marginBottom: "6px" },
  timeInput: { width: "100%", padding: "10px", borderRadius: "8px", fontSize: "14px", boxSizing: "border-box" },
  saveBtn: { width: "100%", padding: "13px", background: "var(--accent)", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px", fontWeight: "bold" },
  greenSaveBtn: { width: "100%", padding: "13px", background: "#4caf50", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontSize: "15px", fontWeight: "bold" },
  currentPhotoWrap: { marginBottom: "16px" },
  currentPhotoLabel: { color: "var(--text-muted)", fontSize: "12px", marginBottom: "8px" },
  currentPhoto: { width: "100%", height: "220px", objectFit: "cover", borderRadius: "10px", border: "1px solid var(--border)", display: "block" },
  removeCurrentBtn: { marginTop: "10px", background: "rgba(233,69,96,0.1)", border: "1px solid rgba(233,69,96,0.3)", color: "var(--accent)", padding: "8px 16px", borderRadius: "8px", cursor: "pointer", fontSize: "13px", width: "100%" },
  uploadPhotoBtn: { flex: 1, padding: "10px", background: "var(--accent)", color: "white", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "13px" },
  cancelPhotoBtn: { padding: "10px 16px", background: "var(--bg-secondary)", color: "var(--text-muted)", border: "1px solid var(--border)", borderRadius: "8px", cursor: "pointer", fontSize: "13px" },
  photoUploadLabel: { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "8px", border: "2px dashed var(--border)", borderRadius: "12px", padding: "32px 20px", cursor: "pointer", background: "var(--bg-secondary)" },
  photoUploadText: { color: "var(--text-secondary)", fontSize: "14px", fontWeight: "600" },
  photoUploadHint: { color: "var(--text-muted)", fontSize: "12px" },
  serviceRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px", background: "var(--bg-secondary)", borderRadius: "8px", marginBottom: "8px", border: "1px solid var(--border)" },
  homeServiceRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "12px", background: "var(--bg-secondary)", borderRadius: "8px", marginBottom: "8px", border: "1px solid rgba(76,175,80,0.2)" },
  serviceInfo: { flex: 1 },
  serviceName: { fontWeight: "bold", color: "white", fontSize: "14px", margin: "0 0 2px" },
  serviceMeta: { color: "var(--text-muted)", fontSize: "12px", margin: 0 },
  serviceActions: { display: "flex", alignItems: "center", gap: "8px" },
  priceEdit: { display: "flex", alignItems: "center", gap: "4px", background: "var(--bg-card)", padding: "4px 8px", borderRadius: "6px", border: "1px solid var(--border)" },
  priceInput: { width: "60px", background: "transparent", border: "none", color: "var(--accent)", fontSize: "14px", fontWeight: "bold", outline: "none" },
  deleteBtn: { background: "rgba(233,69,96,0.1)", border: "1px solid rgba(233,69,96,0.3)", color: "var(--accent)", padding: "6px 10px", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
  editBtnSmall: { background: "rgba(76,175,80,0.1)", border: "1px solid rgba(76,175,80,0.3)", color: "#4caf50", padding: "6px 10px", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
  saveMiniBtn: { background: "rgba(76,175,80,0.15)", border: "1px solid rgba(76,175,80,0.4)", color: "#4caf50", padding: "5px 8px", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
  cancelMiniBtn: { background: "rgba(233,69,96,0.1)", border: "1px solid rgba(233,69,96,0.3)", color: "var(--accent)", padding: "5px 8px", borderRadius: "6px", cursor: "pointer", fontSize: "12px" },
  greenPrice: { color: "#4caf50", fontWeight: "bold", fontSize: "15px" },
  toggleRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "16px" },
  toggleBtnOn: { padding: "9px 20px", background: "rgba(233,69,96,0.1)", border: "1px solid rgba(233,69,96,0.3)", color: "var(--accent)", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "13px", whiteSpace: "nowrap" },
  toggleBtnOff: { padding: "9px 20px", background: "rgba(76,175,80,0.1)", border: "1px solid rgba(76,175,80,0.3)", color: "#4caf50", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", fontSize: "13px", whiteSpace: "nowrap" },
};